import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiEdit3, FiSettings, FiEye, FiUnlock, FiArrowRight, FiStar, FiCheck, FiChevronLeft, FiChevronRight, FiPlay, FiPause } = FiIcons;

const CarouselHowYouStart = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const handleCTAClick = () => {
    window.open('https://register.yiksy.com/', '_blank');
  };

  const steps = [
    {
      icon: FiEdit3,
      title: "Share a Few Details",
      description: "Fill out a short form with some basic info about your company — especially your website and a short description. This helps us personalize your workspace to reflect your brand.",
      number: 1,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "text-blue-600",
      accentColor: "bg-blue-500",
      highlights: [
        "Simple 2-minute form",
        "Add your website and brand info",
        "Helps tailor your workspace"
      ],
      duration: "2 minutes"
    },
    {
      icon: FiSettings,
      title: "We Set Up Your Workspace",
      description: "We'll create your branded workspace with your logo, colors, tone of voice, and a few sample posts. It's a ready-to-use setup you can easily refine and customize later.",
      number: 2,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconColor: "text-purple-600",
      accentColor: "bg-purple-500",
      highlights: [
        "Your logo and colors added",
        "Posts written in your tone",
        "Sample visuals and captions ready to review"
      ],
      duration: "24 hours"
    },
    {
      icon: FiEye,
      title: "Preview Your Content (Free)",
      description: "You'll get viewer access to explore your workspace at no cost. No credit card needed. See how your content looks and get a feel for the platform before making any commitment.",
      number: 3,
      color: "from-green-500 to-green-600",
      bgColor: "bg-gradient-to-br from-green-50 to-green-100",
      iconColor: "text-green-600",
      accentColor: "bg-green-500",
      highlights: [
        "View your sample posts",
        "No credit card required",
        "Try before you commit"
      ],
      duration: "15 days free"
    },
    {
      icon: FiUnlock,
      title: "Unlock Full Access",
      description: "Happy with the preview? Upgrade to start generating unlimited posts, make full edits, and publish across all your platforms — all from one dashboard.",
      number: 4,
      color: "from-amber-500 to-amber-600",
      bgColor: "bg-gradient-to-br from-amber-50 to-amber-100",
      iconColor: "text-amber-600",
      accentColor: "bg-amber-500",
      highlights: [
        "Generate unlimited posts",
        "Edit text, visuals, and layout",
        "Schedule across all major platforms"
      ],
      duration: "Instant access"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, steps.length]);

  const nextStep = () => {
    setActiveStep((prev) => (prev + 1) % steps.length);
  };

  const prevStep = () => {
    setActiveStep((prev) => (prev - 1 + steps.length) % steps.length);
  };

  const goToStep = (index) => {
    setActiveStep(index);
  };

  return (
    <section id="how-you-start" className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-16 sm:py-20 lg:py-28 relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-blue-200/40 via-purple-100/30 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-60 translate-x-1/4 -translate-y-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-r from-purple-200/40 via-blue-100/30 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-50 -translate-x-1/4 translate-y-1/4"></div>

      {/* Floating decorative elements */}
      <motion.div 
        className="absolute top-20 left-20 w-20 h-20 bg-blue-200/20 rounded-full"
        animate={{ y: [-10, 10, -10] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-40 right-20 w-16 h-16 bg-purple-200/20 rounded-full"
        animate={{ y: [10, -10, 10] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16 sm:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-purple-500/10 text-primary px-6 py-3 rounded-full text-sm font-semibold mb-8 backdrop-blur-sm border border-primary/20 shadow-lg">
            <SafeIcon icon={FiStar} className="mr-2" />
            Your Journey Starts Here
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 text-primary leading-tight">
            How You <span className="font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Start</span>
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-text-light max-w-4xl mx-auto leading-relaxed px-4">
            Your journey to effortless content creation begins in four simple steps
          </p>
        </motion.div>

        {/* Main Carousel Container */}
        <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden max-w-6xl mx-auto">
          {/* Carousel Controls */}
          <div className="absolute top-6 right-6 z-20 flex items-center space-x-3">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            >
              <SafeIcon icon={isAutoPlaying ? FiPause : FiPlay} className="text-gray-600" />
            </button>
            <button
              onClick={prevStep}
              className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="text-gray-600" />
            </button>
            <button
              onClick={nextStep}
              className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="text-gray-600" />
            </button>
          </div>

          {/* Step Indicators */}
          <div className="absolute top-6 left-6 z-20 flex space-x-2">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => goToStep(index)}
                className={`w-12 h-3 rounded-full transition-all duration-300 ${
                  index === activeStep ? 'bg-primary shadow-lg' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Main Content Area */}
          <div className="relative h-[500px] sm:h-[600px] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <div className={`${steps[activeStep].bgColor} h-full relative overflow-hidden`}>
                  {/* Background pattern */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/20 opacity-70"></div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/30 rounded-full -translate-x-16 -translate-y-16 filter blur-xl opacity-60"></div>

                  {/* Content Grid */}
                  <div className="grid lg:grid-cols-2 h-full">
                    {/* Left Side - Content */}
                    <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-16">
                      {/* Step Number Badge */}
                      <div className="flex items-center mb-6">
                        <div className={`w-16 h-16 ${steps[activeStep].accentColor} rounded-2xl flex items-center justify-center shadow-lg mr-4`}>
                          <span className="text-white font-bold text-2xl">{steps[activeStep].number}</span>
                        </div>
                        <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
                          <span className="text-sm font-semibold text-gray-600">{steps[activeStep].duration}</span>
                        </div>
                      </div>

                      {/* Title and Description */}
                      <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text mb-6 leading-tight">
                        {steps[activeStep].title}
                      </h3>
                      <p className="text-text-light text-lg sm:text-xl leading-relaxed mb-8">
                        {steps[activeStep].description}
                      </p>

                      {/* Highlights */}
                      <div className="space-y-3 mb-8">
                        {steps[activeStep].highlights.map((highlight, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 + 0.3 }}
                            className="flex items-center"
                          >
                            <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-3 shadow-sm">
                              <SafeIcon icon={FiCheck} className={`${steps[activeStep].iconColor} text-sm`} />
                            </div>
                            <span className="text-text font-medium">{highlight}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Right Side - Visual */}
                    <div className="flex items-center justify-center p-8 sm:p-12">
                      <div className="relative">
                        {/* Large Icon */}
                        <div className="w-32 h-32 sm:w-40 sm:h-40 bg-white rounded-3xl flex items-center justify-center shadow-2xl border border-gray-100 mb-8">
                          <SafeIcon icon={steps[activeStep].icon} className={`${steps[activeStep].iconColor} text-6xl sm:text-7xl`} />
                        </div>

                        {/* Floating Elements */}
                        <motion.div 
                          className="absolute -top-4 -right-4 w-8 h-8 bg-primary rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                        <motion.div 
                          className="absolute -bottom-2 -left-2 w-6 h-6 bg-purple-400 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                        />
                        <motion.div 
                          className="absolute top-1/2 -right-8 w-4 h-4 bg-green-400 rounded-full"
                          animate={{ scale: [1, 1.4, 1] }}
                          transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom Navigation - All Steps Visible */}
          <div className="bg-white border-t border-gray-100 p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {steps.map((step, index) => (
                <motion.button
                  key={index}
                  onClick={() => goToStep(index)}
                  className={`p-4 rounded-xl transition-all duration-300 text-left ${
                    index === activeStep
                      ? `${step.bgColor} border-2 border-primary/30 shadow-lg`
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center mb-2">
                    <div className={`w-8 h-8 ${index === activeStep ? step.accentColor : 'bg-gray-300'} rounded-lg flex items-center justify-center mr-3`}>
                      <SafeIcon icon={step.icon} className={`text-white text-sm`} />
                    </div>
                    <span className="text-sm font-bold text-text">{step.number}</span>
                  </div>
                  <h4 className="font-bold text-text text-sm mb-1 leading-tight">{step.title}</h4>
                  <p className="text-xs text-text-light leading-tight line-clamp-2">{step.duration}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced CTA Section */}
        <motion.div 
          className="text-center mt-16 sm:mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl border border-gray-100 max-w-2xl mx-auto relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500"></div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-100 rounded-full opacity-60"></div>
            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-purple-100 rounded-full opacity-50"></div>

            <div className="relative z-10">
              <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <SafeIcon icon={FiCheck} className="mr-2" />
                Ready to Get Started?
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-text mb-4">
                Start Your Free Preview Today
              </h3>
              <p className="text-text-light mb-8 text-lg">
                No credit card required • Full workspace preview • Upgrade only when ready
              </p>
              <motion.button 
                className="bg-gradient-to-r from-primary to-blue-700 text-white px-10 sm:px-12 py-4 sm:py-5 rounded-xl text-lg font-bold hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-xl flex items-center group mx-auto neon-glow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCTAClick}
              >
                Build My Free Profile
                <SafeIcon icon={FiArrowRight} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-text-light">
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-2 text-green-500" />
                  <span>Free preview</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-2 text-green-500" />
                  <span>No commitment</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-2 text-green-500" />
                  <span>Instant setup</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CarouselHowYouStart;